import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

import { PublicLayout } from '@/components/layout/public-layout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PasswordField } from '@/components/ui/password-field';
import { trackEvent } from '@/lib/analytics';
import { ApiError } from '@/lib/api/errors';
import { isStrongPassword } from '@/lib/validation';

import { signUp } from '../api/auth-api';
import { readJobId, readSource } from '../arrival';
import { clearDraft, readDraft } from '../signup-draft';
import { FORM_ERROR, errorMessage } from '../use-auth-error';
import { useSession } from '../use-session';

interface FinishForm {
  password: string;
  keepMeInformed: boolean;
}

/** Screen two of sign-up: the password, then the account is created. */
export function FinishAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useSession();
  const [draft] = useState(() => readDraft());

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = useForm<FinishForm>({ defaultValues: { password: '', keepMeInformed: false } });
  const password = useWatch({ control, name: 'password' });

  const mutation = useMutation({
    mutationFn: (v: FinishForm) => {
      if (!draft) throw new Error('sign-up draft missing');
      return signUp({
        ...draft,
        password: v.password,
        massMailOptOut: !v.keepMeInformed,
        jobId: readJobId(),
        source: readSource(),
      });
    },
    onSuccess: (token) => {
      clearDraft();
      trackEvent('account_created');
      signIn(token.access_token);
      void navigate('/apply', { replace: true });
    },
  });

  if (!draft) return <Navigate to={{ pathname: '/welcome', search: location.search }} replace />;

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;
  const accountExists =
    mutation.error instanceof ApiError && mutation.error.code === 'account_exists';

  return (
    <PublicLayout
      title="Almost there."
      copy="One last thing: a password, so you can come back to your application any time."
    >
      <div>
        <h3>Almost there, {draft.firstName}</h3>
        <p className="muted">
          <Link to={{ pathname: '/welcome', search: location.search }}>← Edit my details</Link>
        </p>
      </div>
      {mutation.isError && (
        <Alert>
          {errorMessage(mutation.error)}
          {accountExists && (
            <>
              {' '}
              <Link to={{ pathname: '/sign-in', search: location.search }}>
                Pick up where you left off
              </Link>
            </>
          )}
        </Alert>
      )}
      {!mutation.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}

      <form
        className="stack"
        noValidate
        onSubmit={(e) =>
          void handleSubmit((v) => {
            mutation.mutate(v);
          })(e)
        }
      >
        <PasswordField
          id="finish-password"
          label="Create a Password"
          autoComplete="new-password"
          showRequirements
          currentValue={password}
          error={errors.password?.message}
          {...register('password', {
            required: 'Please provide a valid password.',
            validate: (p) => isStrongPassword(p) || 'Please provide a valid password.',
          })}
        />
        <Checkbox
          label="By checking this box, I am giving my permission to MAS Medical Staffing to send me e-mails with new job opportunities and career news."
          {...register('keepMeInformed')}
        />
        <div className="form__actions form__actions--sticky">
          <Button type="submit" block busy={mutation.isPending}>
            Create account &amp; continue
          </Button>
        </div>
      </form>
    </PublicLayout>
  );
}
